import { useEffect, useState } from 'react';
import type { Key } from '@/lib/types';

const TONIC_ORDER: readonly string[] = [
  'C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B',
];

type Result = {
  tonic: string | null;
  mode: string | null;
  setTonic: (t: string) => void;
  setMode: (m: string) => void;
  resetToKey: (keyId: string | null) => void;
  resolvedKeyId: string | null;
  availableTonics: string[];
  availableModes: string[];
  isPairAvailable: (tonic: string, mode: string) => boolean;
};

function findKey(allKeys: readonly Key[], tonic: string | null, mode: string | null): Key | undefined {
  if (tonic === null || mode === null) return undefined;
  return allKeys.find((k) => k.tonic === tonic && k.mode === mode);
}

export function useTonicAndMode(initialKeyId: string | null, allKeys: readonly Key[]): Result {
  const initialKey = initialKeyId !== null ? allKeys.find((k) => k.keyId === initialKeyId) : undefined;
  const [tonic, setTonicState] = useState<string | null>(initialKey?.tonic ?? null);
  const [mode, setModeState] = useState<string | null>(initialKey?.mode ?? null);

  // When the keys list arrives after mount (cold-cache race), re-resolve
  // tonic and mode from initialKeyId if they are still null.
  useEffect(() => {
    if (tonic === null && allKeys.length > 0 && initialKeyId !== null) {
      const key = allKeys.find((k) => k.keyId === initialKeyId);
      if (key) {
        setTonicState(key.tonic);
        setModeState(key.mode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys.length]);

  const resolvedKey = findKey(allKeys, tonic, mode);
  const resolvedKeyId = resolvedKey?.keyId ?? null;

  const availableTonics = TONIC_ORDER.filter((t) =>
    allKeys.some((k) => k.tonic === t),
  );

  const availableModes = [...new Set(allKeys.map((k) => k.mode))].sort();

  const isPairAvailable = (t: string, m: string): boolean =>
    allKeys.some((k) => k.tonic === t && k.mode === m);

  const setTonic = (t: string): void => {
    const newMode = mode !== null && isPairAvailable(t, mode) ? mode : null;
    setTonicState(t);
    setModeState(newMode);
  };

  const setMode = (m: string): void => {
    setModeState(m);
  };

  const resetToKey = (keyId: string | null): void => {
    const key = keyId !== null ? allKeys.find((k) => k.keyId === keyId) : undefined;
    setTonicState(key?.tonic ?? null);
    setModeState(key?.mode ?? null);
  };

  return {
    tonic,
    mode,
    setTonic,
    setMode,
    resetToKey,
    resolvedKeyId,
    availableTonics,
    availableModes,
    isPairAvailable,
  };
}
